import { useEffect, useRef, useState } from "preact/hooks";
import type { ChatLine } from "~/protocol";
import type { Channel } from "~/web/src/channel";
import { CredentialsCard } from "~/web/src/project/credentials-card";
import { cardTitle, hostOf, stepText } from "~/web/src/project/tool-names";

// The transcript as a feed (mockup ux/06): the user's messages as bubbles, the
// director's replies as plain prose, and everything the agents *did* folded
// into collapsible cards. Grouping is derived from the lines alone: a run of
// director tool calls and patches between two messages is one card; the
// explorer takeover — from the spawn call until the director's next line — is
// one card with the explorer's own lines as its steps. The pending ask renders
// below the cards, outside them, so it is never hidden in a collapsed one.

export type Item =
  | { kind: "user"; line: ChatLine }
  | { kind: "agent"; line: ChatLine }
  | { kind: "card"; lines: ChatLine[]; explorer: boolean };

export function groupLines(lines: ChatLine[]): Item[] {
  const items: Item[] = [];
  let card: Extract<Item, { kind: "card" }> | null = null;
  for (const line of lines) {
    if (card?.explorer && line.agent !== "director") {
      card.lines.push(line);
      continue;
    }
    if (line.body.kind === "message") {
      card = null;
      items.push({ kind: line.agent === "user" ? "user" : "agent", line });
      continue;
    }
    const spawns = line.body.kind === "tool_call" && line.body.name === "spawn_explorer_agent";
    if (!card || spawns || card.explorer) {
      card = { kind: "card", lines: [line], explorer: spawns };
      items.push(card);
    } else {
      card.lines.push(line);
    }
  }
  return items;
}

const seconds = (a: string, b: string) => Math.max(0, (Date.parse(b) - Date.parse(a)) / 1000);
function mmss(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

/** The wall clock, ticking once a second while `on`. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

function Check() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="3.5"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Card({
  item,
  busy,
  host,
  progress,
}: {
  item: Extract<Item, { kind: "card" }>;
  busy: boolean;
  host: string;
  progress: Channel["progress"]["value"];
}) {
  const first = item.lines[0];
  const last = item.lines[item.lines.length - 1];
  const now = useNow(busy);
  if (!first || !last) return null;
  const elapsed = busy
    ? seconds(first.at, new Date(now).toISOString())
    : seconds(first.at, last.at);
  return (
    <details class="act">
      <summary class="head">
        <span class={`st ${busy ? "busy" : "done"}`}>{!busy && <Check />}</span>
        <span>{cardTitle(item.lines, item.explorer, busy, host)}</span>
        <span class="meta">{mmss(elapsed)}</span>
        <svg
          aria-hidden="true"
          class="chev"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </summary>
      <div class="body">
        {busy && progress && "seconds" in progress && (
          <div class="prog">
            <div class="bar">
              <i key={progress.key} style={{ animationDuration: `${progress.seconds}s` }} />
            </div>
            <div class="l">
              <span>{progress.task}</span>
              <span>about {Math.max(1, Math.round(progress.seconds / 60))} min</span>
            </div>
          </div>
        )}
        {busy && progress && "ahead" in progress && (
          <div class="prog">
            <div class="l">
              <span>
                <span class="spin" />{" "}
                {progress.task === "browser"
                  ? "waiting for a browser"
                  : `${progress.task}: waiting for a free render slot`}
              </span>
              <span>{progress.ahead > 0 ? `${progress.ahead} ahead` : "next up"}</span>
            </div>
          </div>
        )}
        <ul class="steps">
          {item.lines.map((l, i) => {
            const { label, detail } = stepText(l);
            const cur = busy && i === item.lines.length - 1;
            return (
              <li key={l.id} class={cur ? "cur" : ""}>
                <span class="t">{mmss(seconds(first.at, l.at))}</span>
                <i class="d" />
                <span>
                  {label}
                  {detail && <code>{detail}</code>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}

export function Feed({ channel, items }: { channel: Channel; items: Item[] }) {
  const lines = channel.chat.value;
  const agent = channel.agent.value;
  const progress = channel.progress.value;
  const host = hostOf(channel.project.value?.targetUrl ?? "");
  const scroll = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scroll.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length, agent.status]);
  // The composer growing with its draft takes the feed's room: when the feed
  // was showing its latest lines it keeps showing them, scrolled-up history
  // stays put. "Was" is read off the height before the resize — the content and
  // the scroll position are still the old ones when the observer fires.
  useEffect(() => {
    const el = scroll.current;
    if (!el) return;
    let height = el.clientHeight;
    const resized = new ResizeObserver(() => {
      const atBottom = el.scrollHeight - el.scrollTop - height < 4;
      height = el.clientHeight;
      if (atBottom) el.scrollTop = el.scrollHeight;
    });
    resized.observe(el);
    return () => resized.disconnect();
  }, []);

  const last = items[items.length - 1];
  const ask = agent.status === "awaiting_user" ? agent.ask : undefined;
  // Between the user's message and the first tool call the model is thinking.
  const thinking = agent.status === "running" && last?.kind !== "card";

  return (
    <div class="feed" ref={scroll}>
      {items.map((item, i) => {
        if (item.kind === "user") {
          return (
            <div class="msg user" key={item.line.id}>
              {item.line.body.kind === "message" && item.line.body.text}
            </div>
          );
        }
        if (item.kind === "agent") {
          return (
            <div class={`msg agent ${item.line.agent}`} key={item.line.id}>
              {item.line.body.kind === "message" && item.line.body.text}
            </div>
          );
        }
        return (
          <Card
            key={item.lines[0]?.id}
            item={item}
            busy={i === items.length - 1 && agent.status !== "idle"}
            host={host}
            progress={progress}
          />
        );
      })}
      {thinking && (
        <div class="act">
          <div class="head">
            <span class="st busy" />
            <span>Thinking…</span>
          </div>
        </div>
      )}
      {ask?.kind === "question" && <div class="msg agent prompt">{ask.prompt}</div>}
      {ask?.kind === "credentials" && (
        <CredentialsCard channel={channel} domain={ask.domain} note={ask.prompt} />
      )}
    </div>
  );
}
